import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { TSupportedProviders } from '@simOn/common/providers';
import {
  ApartmentAuthorizationConnectionRequestInterface,
  ApartmentAuthorizationConnectionsRequestInterface,
  IAuthorizationDeletionData,
  IProviderInterface,
  ProviderConnectionsStatus,
  SupportedProviderDisplayConfig,
  TryDeleteApartmentAuthorizationResponseInterface
} from '@simOn/provider/connection/models';
import { requestToHttpParamsMapper } from '@simOn/utils/helpers';
import { API_URL } from '@simOn/utils/tokens';
import { Observable } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class ProvidersApiService {
  private readonly http = inject(HttpClient);
  private readonly _apiUrl = inject(API_URL);

  getSupportedProviders(apartmentId?: string): Observable<SupportedProviderDisplayConfig<TSupportedProviders>[]> {
    return this.http.get<SupportedProviderDisplayConfig<TSupportedProviders>[]>(
      `${this._apiUrl}/Providers/GetSupportedProviders${apartmentId ? '?apartmentId=' + apartmentId : ''}`
    );
  }

  addApartmentAuthorizations(data: ApartmentAuthorizationConnectionsRequestInterface): Observable<void> {
    return this.http.post<void>(`${this._apiUrl}/Providers/AddApartmentAuthorizations`, data);
  }

  getProviderConnectionStatus<T extends Object>(
    provider: T,
    controller: string
  ): Observable<ProviderConnectionsStatus> {
    const params = requestToHttpParamsMapper(provider);

    return this.http.get<ProviderConnectionsStatus>(`${this._apiUrl}/${controller}/GetProviderConnectionStatus`, {
      params
    });
  }

  tryDeleteApartmentAuthorization(
    apartmentAuthorizations: IProviderInterface[]
  ): Observable<TryDeleteApartmentAuthorizationResponseInterface> {
    return this.http.post<TryDeleteApartmentAuthorizationResponseInterface>(
      `${this._apiUrl}/Providers/TryDeleteApartmentAuthorizations`,
      { apartmentAuthorizations }
    );
  }

  forceDeleteApartmentAuthorization(
    tryDeleteApartmentAuthorizationRequest: ApartmentAuthorizationConnectionRequestInterface[]
  ): Observable<void> {
    return this.http.post<void>(`${this._apiUrl}/Providers/ForceDeleteApartmentAuthorizations`, {
      apartmentAuthorizations: tryDeleteApartmentAuthorizationRequest
    });
  }

  loginApartmentAuthorization<T>(data: T, controller: string): Observable<void> {
    return this.http.post<void>(`${this._apiUrl}/${controller}/loginApartmentAuthorization`, data);
  }

  logoutApartmentAuthorization<T>(data: T, controller: string): Observable<void> {
    return this.http.post<void>(`${this._apiUrl}/${controller}/logoutApartmentAuthorization`, data);
  }

  countElementsToBeDeleted<T>(data: T, controller: string): Observable<IAuthorizationDeletionData> {
    return this.http.post<IAuthorizationDeletionData>(`${this._apiUrl}/${controller}/CountElementsToBeDeleted`, data);
  }

  deleteApartmentAuthorization<T, K = void>(data: T, controller: string): Observable<K> {
    return this.http.delete<K>(`${this._apiUrl}/${controller}/DeleteApartmentAuthorization?${data}`);
  }

  addApartmentAuthorization<T>(data: T, controller: string): Observable<void> {
    return this.http.post<void>(`${this._apiUrl}/${controller}/AddApartmentAuthorization`, data);
  }

  changeServerUrl<T>(data: T, controller: string): Observable<void> {
    return this.http.put<void>(`${this._apiUrl}/${controller}/ChangeServerUrl`, data);
  }
}
